import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'

// ── Custom cursor — matches landing page exactly ──
// Only initialised on devices that actually have a mouse. On touch devices
// (phones, tablets) the cursor would otherwise be stuck at the first tap
// position with no way to move it, since there are no mousemove events.
//
// We use multiple checks (matchMedia + ontouchstart + maxTouchPoints) because
// some hybrid devices and iOS Safari versions lie about pointer/hover
// capabilities. If any of these say "this is a touch device", we bail.
function isTouchDevice() {
  if (typeof window === 'undefined') return false

  // Explicit touch-device signals
  if ('ontouchstart' in window) return true
  if (navigator.maxTouchPoints > 0) return true
  if (navigator.msMaxTouchPoints > 0) return true

  // Media query check — only trust this when the above are inconclusive
  const coarsePointer = window.matchMedia && window.matchMedia('(pointer: coarse)').matches
  const noHover       = window.matchMedia && window.matchMedia('(hover: none)').matches
  if (coarsePointer || noHover) return true

  return false
}

function initCursor() {
  // Bail on any signal that this is a touch device
  if (isTouchDevice()) return

  // Additional positive check — require both hover AND fine pointer
  const hasMouse =
    window.matchMedia &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches

  if (!hasMouse) return

  const dot  = document.createElement('div')
  const ring = document.createElement('div')
  dot.className  = 'tb-cursor'
  ring.className = 'tb-cursor-ring'

  // Start both off-screen so they don't flash at 0,0
  dot.style.left  = '-100px'
  dot.style.top   = '-100px'
  ring.style.left = '-100px'
  ring.style.top  = '-100px'

  document.body.appendChild(dot)
  document.body.appendChild(ring)

  let mx = -100, my = -100
  let rx = -100, ry = -100

  document.addEventListener('mousemove', e => {
    mx = e.clientX
    my = e.clientY
    dot.style.left = mx + 'px'
    dot.style.top  = my + 'px'
  })

  // If a touch event ever fires (e.g. user plugs in a touchscreen monitor
  // mid-session, or the initial detection failed), remove the cursor
  // entirely and restore native cursors on the page.
  document.addEventListener('touchstart', () => {
    dot.remove()
    ring.remove()
    document.documentElement.classList.add('tb-no-custom-cursor')
  }, { once: true, passive: true })

  // Ring lags behind with lerp — same coordinate system as dot
  ;(function loop() {
    rx += (mx - rx) * 0.12
    ry += (my - ry) * 0.12
    ring.style.left = Math.round(rx) + 'px'
    ring.style.top  = Math.round(ry) + 'px'
    requestAnimationFrame(loop)
  })()

  // Expand ring on interactive elements
  function bindHover(el) {
    if (el.dataset.cursorBound) return
    el.dataset.cursorBound = '1'
    el.addEventListener('mouseenter', () => {
      ring.style.width  = '52px'
      ring.style.height = '52px'
      ring.style.borderColor = 'rgba(232,255,71,.65)'
    })
    el.addEventListener('mouseleave', () => {
      ring.style.width  = '36px'
      ring.style.height = '36px'
      ring.style.borderColor = 'rgba(232,255,71,.4)'
    })
  }

  // Watch for React rendering new elements
  const observer = new MutationObserver(() => {
    document.querySelectorAll(
      'a, button, input, label, [onclick], [role="button"], ' +
      '.ob-option, .tree-option, .module-row, .lesson-row, .quiz__option'
    ).forEach(bindHover)
  })
  observer.observe(document.body, { childList: true, subtree: true })
}

// If we're on a touch device, mark the document root so CSS can restore
// native cursors everywhere (belt-and-braces alongside the media query).
if (isTouchDevice()) {
  document.documentElement.classList.add('tb-no-custom-cursor')
}

initCursor()

const root = ReactDOM.createRoot(document.getElementById('root'))
root.render(<App />)
